import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import {
  DEFAULT_SCORE_WEIGHTS,
  SCORE_AXES,
  type ScoreAxis,
  type ScoreWeights,
} from "@resume-cloudflare/shared";
import { FileText, RefreshCcw, RotateCcw, Save } from "lucide-react";
import { useResumes, type ResumeRow } from "../lib/resumes";
import { useMe, usePatchMe, useRescoreAll } from "../lib/me";
import { Button } from "../components/ui/Button";
import { Card, CardBody, CardHeader } from "../components/ui/Card";
import { EmptyState } from "../components/ui/EmptyState";
import { Pill } from "../components/ui/Pill";
import { Skeleton } from "../components/ui/Skeleton";

const AXIS_LABELS: Record<ScoreAxis, string> = {
  responsibilities: "Responsibilities",
  seniority: "Seniority",
  domain: "Domain",
  tech_stack: "Tech stack",
  location_fit: "Location fit",
};

const AXIS_DESCRIPTIONS: Record<ScoreAxis, string> = {
  responsibilities: "How closely the day-to-day work matches your target role.",
  seniority: "Whether the level (IC vs lead, years required) lines up.",
  domain: "Industry / customer-segment overlap with your background.",
  tech_stack: "Familiarity with the platforms, languages, and tools listed.",
  location_fit: "Computed from your location prefs vs the JD's stated policy.",
};

export default function Settings() {
  return (
    <section className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6 lg:p-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-1 text-sm text-neutral-400">
          Pick the resume to score against, tune the weights, and trigger a
          full rescore when you change either.
        </p>
      </header>

      <PrimaryResumeCard />
      <ScoreWeightsCard />
      <RescoreCard />
    </section>
  );
}

function PrimaryResumeCard() {
  const me = useMe();
  const resumes = useResumes();
  const patch = usePatchMe("Primary resume updated.");

  const loading = me.isLoading || resumes.isLoading;
  const error = me.error ?? resumes.error;

  return (
    <Card>
      <CardHeader
        title="Primary resume"
        description="Used for fit scoring, fit summaries, and cover-letter drafts."
      />
      <CardBody>
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : error ? (
          <EmptyState
            tone="danger"
            title="Couldn't load resumes"
            description="Try refreshing in a moment."
          />
        ) : (resumes.data?.resumes.length ?? 0) === 0 ? (
          <EmptyState
            icon={<FileText size={16} aria-hidden />}
            title="No resumes uploaded yet"
            description="Upload a PDF on the Resumes page to enable scoring."
            action={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  window.location.href = "/resumes";
                }}
              >
                Go to Resumes
              </Button>
            }
          />
        ) : (
          <ul className="space-y-1">
            {resumes.data!.resumes.map((r) => (
              <ResumeRadioRow
                key={r.id}
                resume={r}
                checked={me.data?.prefs.primary_resume_id === r.id}
                disabled={patch.isPending}
                onSelect={() => patch.mutate({ primary_resume_id: r.id })}
              />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function ResumeRadioRow({
  resume,
  checked,
  disabled,
  onSelect,
}: {
  resume: ResumeRow;
  checked: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <label
        className={
          "flex cursor-pointer items-center gap-3 rounded-md border border-neutral-800 bg-neutral-900/40 px-3 py-2 text-sm transition-colors hover:border-neutral-700" +
          (checked ? " border-accent-500/60 bg-neutral-900/80" : "")
        }
      >
        <input
          type="radio"
          name="primary-resume"
          checked={checked}
          disabled={disabled}
          onChange={onSelect}
          className="h-4 w-4 accent-accent-500"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-neutral-100">{resume.label}</p>
          <p className="text-xs text-neutral-500">
            {new Date(resume.uploaded_at).toLocaleDateString()} ·{" "}
            {(resume.bytes / 1024).toFixed(1)} KB
          </p>
        </div>
        {checked ? <Pill tone="accent">Active</Pill> : null}
      </label>
    </li>
  );
}

function ScoreWeightsCard() {
  const me = useMe();
  const patch = usePatchMe("Weights updated. Re-sorting jobs.");

  const serverWeights: ScoreWeights = useMemo(
    () => me.data?.prefs.score_weights ?? { ...DEFAULT_SCORE_WEIGHTS },
    [me.data?.prefs.score_weights],
  );
  const [local, setLocal] = useState<ScoreWeights>(serverWeights);

  // Reset local state whenever the server snapshot changes (initial load,
  // or a save lands and refetches).
  useEffect(() => setLocal(serverWeights), [serverWeights]);

  const sum = SCORE_AXES.reduce((acc, axis) => acc + (local[axis] ?? 0), 0);
  const isValid = Math.abs(sum - 1) <= 0.01;
  const dirty = SCORE_AXES.some(
    (axis) => Math.abs((local[axis] ?? 0) - (serverWeights[axis] ?? 0)) > 0.001,
  );
  const canSave = isValid && dirty && !patch.isPending;

  function setAxis(axis: ScoreAxis, value: number) {
    setLocal((prev) => ({ ...prev, [axis]: value }));
  }

  return (
    <Card>
      <CardHeader
        title="Score weights"
        description="Composite score = weighted sum across the 5 axes. Changing weights re-derives sort order in pure SQL — no AI re-inference."
      />
      <CardBody>
        {me.isLoading ? (
          <div className="space-y-3">
            {SCORE_AXES.map((axis) => (
              <Skeleton key={axis} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {SCORE_AXES.map((axis) => (
                <WeightSlider
                  key={axis}
                  axis={axis}
                  value={local[axis] ?? 0}
                  onChange={(v) => setAxis(axis, v)}
                />
              ))}
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-neutral-800 pt-4">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-neutral-500">Sum</span>
                <span className="font-mono tabular-nums text-neutral-200">
                  {sum.toFixed(2)}
                </span>
                {isValid ? (
                  <Pill tone="success">valid</Pill>
                ) : (
                  <Pill tone="warning">must equal 1.00 ±0.01</Pill>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setLocal({ ...DEFAULT_SCORE_WEIGHTS })}
                  leadingIcon={<RotateCcw size={12} aria-hidden />}
                >
                  Reset to defaults
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => patch.mutate({ score_weights: local })}
                  loading={patch.isPending}
                  disabled={!canSave}
                  leadingIcon={<Save size={12} aria-hidden />}
                >
                  Save weights
                </Button>
              </div>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function WeightSlider({
  axis,
  value,
  onChange,
}: {
  axis: ScoreAxis;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-neutral-100">
            {AXIS_LABELS[axis]}
          </p>
          <p className="text-xs text-neutral-500">{AXIS_DESCRIPTIONS[axis]}</p>
        </div>
        <span className="font-mono text-sm tabular-nums text-neutral-200">
          {value.toFixed(2)}
        </span>
      </div>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={`${AXIS_LABELS[axis]} weight`}
        className="mt-2 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-neutral-800 accent-accent-500"
      />
    </div>
  );
}

function RescoreCard() {
  const me = useMe();
  const rescore = useRescoreAll();
  const hasPrimary = !!me.data?.prefs.primary_resume_id;

  return (
    <Card>
      <CardHeader
        title="Rescore all active jobs"
        description="Enqueues every active job that hasn't been scored against the current primary resume. Already-scored rows are skipped."
      />
      <CardBody className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-md text-xs text-neutral-500">
          Use after switching primary resume. The queue drains at ~7–9
          jobs/min single-concurrency, so a full catalog refresh takes hours.
          Watch progress on{" "}
          <Link to="/runs" className="underline-offset-4 hover:underline">
            Runs
          </Link>
          .
        </p>
        <Button
          variant="primary"
          size="sm"
          onClick={() => rescore.mutate()}
          loading={rescore.isPending}
          disabled={!hasPrimary || rescore.isPending}
          leadingIcon={<RefreshCcw size={12} aria-hidden />}
        >
          Rescore all
        </Button>
      </CardBody>
    </Card>
  );
}
